import { Zap, Tag, FileText, Settings, Coins, Check, ArrowRight, Users, Building2, Phone, Mail, BookOpen, Sparkles } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export const FeatureBento = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 max-w-6xl mx-auto">
      {/* Hero feature 1 — AI Extraction (spans 2 cols) */}
      <BigCard
        index={0}
        className="lg:col-span-2"
        icon={Zap}
        title="AI Extraction"
        body="Parses signature blocks and email context to capture name, company, title, phone, email, website and address."
        pain="No manual mapping. No setup workflows."
      >
        <ExtractionViz />
      </BigCard>

      {/* Hero feature 2 — Smart Categories */}
      <BigCard
        index={1}
        icon={Tag}
        title="Smart Categories"
        body="Auto-assigns your custom categories such as Customer, Partner, Supplier or Lead."
        pain="No generic CRM schemas."
      >
        <CategoryViz />
      </BigCard>

      {/* Smart Notes — full width row */}
      <BigCard
        index={2}
        className="lg:col-span-3"
        icon={BookOpen}
        title="Smart Notes"
        body="Concise, task-based research notes so you remember who the contact is and why they matter — pulled live from public sources."
        pain="No more tab-switching for context."
      >
        <SmartNotesViz />
      </BigCard>

      {/* Compact tiles row */}
      <SmallCard index={3} icon={Settings} title="Field Presets">
        Apply default values like country code or department for a consistent address book.
      </SmallCard>
      <SmallCard index={4} icon={Coins} title="Adaptive Credits">
        Pay only for what you use. No mandatory subscription. Top-ups any time.
      </SmallCard>
      <SmallCard index={5} icon={FileText} title="Reliable Logging">
        Every extraction is logged so you can review, retry or audit anytime.
      </SmallCard>
    </div>
  );
};

const BigCard = ({
  index,
  className = "",
  icon: Icon,
  title,
  body,
  pain,
  children,
}: {
  index: number;
  className?: string;
  icon: typeof Zap;
  title: string;
  body: string;
  pain: string;
  children: React.ReactNode;
}) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 80,
    threshold: 0.1,
  });

  return (
    <div ref={ref} className={`${animationClass} ${className}`}>
      <div className="fluent-tile fluent-tile-hover p-6 md:p-8 h-full flex flex-col bg-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-surface-2 border border-border/60 grid place-items-center">
            <Icon className="w-5 h-5 text-foreground" />
          </div>
          <h3 className="text-xl font-display font-semibold">{title}</h3>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-2">{body}</p>
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground/70 line-through mb-5">
          {pain}
        </p>

        <div className="mt-auto rounded-lg bg-surface-1 border border-border/60 p-4">{children}</div>
      </div>
    </div>
  );
};

const SmallCard = ({
  index,
  icon: Icon,
  title,
  children,
}: {
  index: number;
  icon: typeof Zap;
  title: string;
  children: React.ReactNode;
}) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 80,
    threshold: 0.1,
  });

  return (
    <div ref={ref} className={animationClass}>
      <div className="fluent-tile fluent-tile-hover p-5 md:p-6 h-full bg-card flex flex-col">
        <div className="w-9 h-9 rounded-lg bg-surface-2 border border-border/60 grid place-items-center mb-4">
          <Icon className="w-4.5 h-4.5 text-foreground" strokeWidth={1.75} />
        </div>
        <h3 className="text-base font-display font-semibold mb-1.5">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
      </div>
    </div>
  );
};

function ExtractionViz() {
  const fields = [
    { Icon: Users, k: "NAME", v: "Sarah Mitchell" },
    { Icon: Building2, k: "COMPANY", v: "Northwind Labs" },
    { Icon: Phone, k: "PHONE", v: "+1 415 555 0142" },
    { Icon: Mail, k: "EMAIL", v: "sarah@northwind.io" },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {fields.map(({ Icon, k, v }, i) => (
        <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-card border border-border/60">
          <Icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground">{k}</div>
            <div className="text-[10px] font-medium truncate">{v}</div>
          </div>
          <Check className="w-3 h-3 text-success" />
        </div>
      ))}
    </div>
  );
}

function CategoryViz() {
  const cats = [
    { name: "Customer", color: "bg-brand-blue/15 text-brand-blue border-brand-blue/30" },
    { name: "Partner", color: "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/30" },
    { name: "Lead", color: "bg-brand-orange/15 text-brand-orange border-brand-orange/30" },
    { name: "Supplier", color: "bg-muted-foreground/10 text-muted-foreground border-border" },
  ];
  return (
    <div className="space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
        Auto-assigned
      </div>
      <div className="flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <span
            key={c.name}
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${c.color}`}
          >
            {c.name}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground pt-1">
        <ArrowRight className="w-3 h-3" />
        Northwind Labs → <span className="font-medium text-foreground">Partner</span>
      </div>
    </div>
  );
}

function SmartNotesViz() {
  const bullets = [
    "Series B funding, $40M raised in 2024",
    "B2B logistics platform · partners with FedEx",
    "Key competitors: Flexport, Convoy",
  ];
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
          Note · Northwind Labs
        </div>
        <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-brand-blue">
          <Sparkles className="w-2.5 h-2.5" />
          Pro
        </div>
      </div>
      <ul className="space-y-1.5">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-[11px] text-foreground/90 leading-snug">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-blue flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

